import React from 'react';

import {
  FlowContextProvider,
  StepProps,
  StepWrapper,
  toCentsFromString,
  toMoney,
  useMutation,
  useQuery,
} from '@betterment/js-runtime';
import { Helmet } from 'react-helmet';
import { Route, Routes, useMatch, useSearchParams } from 'react-router-dom';

import DepositFlowAmount from './Amount';
import DepositFlowConfirm from './Confirm';
import { DepositValues } from './data';

import { useAccount } from '../Checking/components/AccountProvider';
import { useCustomer } from '../Checking/components/CustomerProvider';
import { DepositParams, TransferAvailabilityEstimate } from '../Checking/data';
import { CloseButton, ModalContent, ModalOverlay } from '../Checking/modal-helpers';
import { externalAccountCreationPath } from '../Checking/path-helpers';
import FullPageLoader from '../Shared/components/FullPageLoader';
import SuccessStep from '../Shared/components/SuccessStep';
import { locationAssign } from '../Shared/path-helpers';
import { urlBuilder } from '../Shared/urlBuilder';

export const depositValues: { externalAccountId: number | null; amount: string } = {
  externalAccountId: null,
  amount: '',
};

const DepositFlowController: React.FC = () => {
  const { customer } = useCustomer();
  const { account } = useAccount();

  const handleClose = () => locationAssign(urlBuilder.getAppCheckingSummary.absolute({ accountId: account.id }));
  const { loading: isEstimateLoading, data: availabilityEstimate } = useQuery<TransferAvailabilityEstimate>(
    urlBuilder.getDepositAvailabilityEstimate()
  );
  const accountId = account.id;
  const [postDeposit] = useMutation<{}, DepositParams>(urlBuilder.postDeposit(accountId));

  const [searchParams] = useSearchParams();
  const amount = searchParams.get('amount') || '';

  const hasExternalAccount = !!customer.externalAccount;

  const onDeposit = (values: DepositValues) => {
    return postDeposit({
      amountCents: toMoney(toCentsFromString(values.amount)).cents,
      externalAccountId: values.externalAccountId,
    });
  };

  const match = useMatch('/checking/accounts/:accountId/deposit_setup/*');

  React.useEffect(() => {
    if (!hasExternalAccount) locationAssign(externalAccountCreationPath(accountId, 'deposit'));
  }, [accountId, hasExternalAccount]);

  if (isEstimateLoading || customer.externalAccount == null) {
    return <FullPageLoader />;
  }

  return (
    <ModalOverlay>
      <Helmet>
        <title>Deposit</title>
      </Helmet>
      <ModalContent aria-label="make a deposit">
        <CloseButton closeHandler={handleClose} />
        <FlowContextProvider<DepositValues>
          initialValues={{
            ...depositValues,
            amount,
            externalAccountId: customer.externalAccount?.id,
          }}
          returnToPath={urlBuilder.getAppCheckingSummary.absolute({ accountId })}
          basePath={match!.pathnameBase}
          routePath=""
        >
          <Routes>
            <Route
              path="/amount"
              element={
                <StepWrapper name="amount">
                  {(props: StepProps<DepositValues>) => <DepositFlowAmount {...props} customer={customer} />}
                </StepWrapper>
              }
            />
            <Route
              path="/confirm"
              element={
                <StepWrapper name="confirm">
                  {(props: StepProps<DepositValues>) => (
                    <DepositFlowConfirm
                      {...props}
                      customer={customer}
                      availabilityEstimate={availabilityEstimate!}
                      onDeposit={onDeposit}
                    />
                  )}
                </StepWrapper>
              }
            />
            <Route
              path="/success"
              element={
                <StepWrapper name="success">
                  <SuccessStep
                    message="Your deposit has been submitted"
                    eventProperties={{ name: 'DepositSuccess', flow: 'DepositToChecking' }}
                  />
                </StepWrapper>
              }
            />
          </Routes>
        </FlowContextProvider>
      </ModalContent>
    </ModalOverlay>
  );
};

export default DepositFlowController;
